export type NativeContentsSuppressionPreparation = () => Promise<void>;

/**
 * Coordinates native owners without exposing Electron types to UI hosts.
 * A snapshot of the current participants is used so mount/unmount during an
 * in-flight preparation cannot mutate the batch being awaited. Concurrent
 * requests share one serialized loop; a request arriving mid-batch queues one
 * follow-up pass instead of racing layer transition state.
 */
export class NativeContentsSuppressionCoordinator {
  private readonly preparations =
    new Set<NativeContentsSuppressionPreparation>();
  private preparing: Promise<void> | undefined;
  private prepareQueued = false;

  register(prepare: NativeContentsSuppressionPreparation): () => void {
    this.preparations.add(prepare);
    return () => this.preparations.delete(prepare);
  }

  prepare(): Promise<void> {
    this.prepareQueued = true;
    if (this.preparing) {
      return this.preparing;
    }
    const run = async (): Promise<void> => {
      while (this.prepareQueued) {
        this.prepareQueued = false;
        await Promise.allSettled(
          [...this.preparations].map((prepare) => prepare()),
        );
      }
    };
    const promise = run().finally(() => {
      if (this.preparing === promise) {
        this.preparing = undefined;
      }
    });
    this.preparing = promise;
    return promise;
  }
}
